import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import { Prompt } from "react-router";

import Header from "components/library/Header";
import IconButton from "components/library/IconButton";
import MessagePanel from "components/library/MessagePanel";
import Dropdown from "components/library/Dropdown";
import {
  initializeDeepDiveForm,
  hydrateDeepDiveForm,
} from "deepDiveEditor/actions/form";
import { deepDiveInitialState } from "deepDiveEditor/reducers/form";
import {
  DEEP_DIVE_EDITOR_LINKS,
  DEEP_DIVE_UNSAVED_CHANGES_MESSAGE,
} from "config/constants/deepDiveEditor";
import SaveButton from "deepDiveEditor/containers/SaveButton";
import DeepDiveEditorCTA from "deepDiveEditor/containers/DeepDiveEditorCTA";
import DeepDiveEditor from "deepDiveEditor/containers/DeepDiveEditor";
import DeepDiveMetadata from "deepDiveEditor/containers/DeepDiveMetadata";
import EditorSidebar from "editor/containers/EditorSidebar";
import EditorLoading from "editor/components/EditorLoading";
// NOTE: reusing styles from EditorContainer
import styles from "../../../editor/containers/EditorContainer/EditorContainer.css";
class DeepDiveEditorContainer extends React.Component {
  constructor(props) {
    super(props);
    this.getBaseUrl = this._getBaseUrl.bind(this);
    this.promptForUnsavedChanges = this._promptForUnsavedChanges.bind(this);
    this.state = { title: null };
  }

  _getBaseUrl(id) {
    const idForUrl = id || this.props.form.id;
    if (idForUrl) {
      return `/editor/deep-dive/${idForUrl}/`;
    } else {
      return `/editor/deep-dive/`;
    }
  }

  _promptForUnsavedChanges(e) {
    if (this.props.status.isDirty || this.props.status.isSaving) {
      // See https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onbeforeunload#example
      e.preventDefault();
      e.returnValue = "";
    }
  }

  UNSAFE_componentWillMount() {
    this.props.initializeDeepDiveForm(deepDiveInitialState);
  }

  componentDidMount() {
    window.addEventListener("beforeunload", this.promptForUnsavedChanges);

    if (this.props.match.params.id) {
      this.props.hydrateDeepDiveForm(this.props.match.params.id).then(() => {
        this.setState({
          title: this.props.form.slug,
        });
      });
    }
  }

  componentDidUpdate(prevProps) {
    if (
      !prevProps.match.params.id &&
      !prevProps.form.id &&
      this.props.form.id
    ) {
      prevProps.history.replace(this.getBaseUrl(this.props.form.id));
    }
  }

  componentWillUnmount() {
    window.removeEventListener("beforeunload", this.promptForUnsavedChanges);
  }

  render() {
    const editorFields = {
      metadata: {
        fields: [
          "slug",
          "outline_color",
          "bg_color",
          "text_color",
          "sponsored_text",
        ],
      },
      content: { fields: ["front_cover", "stories"] },
    };
    const defaultPage = "content";
    const editorProps =
      editorFields[this.props.match.params.action || defaultPage];

    return (
      <>
        <Prompt
          message={location =>
            location.pathname.startsWith("/editor/deep-dive")
              ? true
              : DEEP_DIVE_UNSAVED_CHANGES_MESSAGE
          }
          when={this.props.status.isDirty || this.props.status.isSaving}
        />
        <Helmet>
          <title>{this.state.title}</title>
        </Helmet>

        <Header
          className={styles.header}
          left={
            <IconButton
              className="gtm-close-out-of-story"
              name="x"
              to={"/curation"}
            />
          }
          center={null}
          right={
            <div className={styles.buttons}>
              <SaveButton />
              <DeepDiveEditorCTA history={this.props.history} />
            </div>
          }
        />
        <MessagePanel />
        <div className={styles.editorTopbar}>
          <Dropdown
            inputId="deepDiveEditorLinks"
            onChange={option => {
              this.props.history.push(this.getBaseUrl() + option.value);
            }}
            options={DEEP_DIVE_EDITOR_LINKS.map(({ text, to }) => ({
              className: classNames({
                [styles.error]: this.props.form.errors[text.toLowerCase()],
              }),
              label: text,
              value: to,
            }))}
            value={this.props.match.params.action || defaultPage}
          />
        </div>
        <div className={styles.editor}>
          <EditorSidebar
            links={DEEP_DIVE_EDITOR_LINKS}
            form={this.props.form}
            getBaseUrl={this.getBaseUrl}
          />
          {this.props.ui.isLoading ? (
            <EditorLoading />
          ) : this.props.match.params.action === "metadata" ? (
            <DeepDiveMetadata />
          ) : (
            <DeepDiveEditor {...editorProps} />
          )}
        </div>
      </>
    );
  }
}

DeepDiveEditorContainer.propTypes = {
  form: PropTypes.object.isRequired,
  hydrateDeepDiveForm: PropTypes.func.isRequired,
  initializeDeepDiveForm: PropTypes.func.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
      action: PropTypes.string,
    }),
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
    replace: PropTypes.func,
  }).isRequired,
  status: PropTypes.object,
  ui: PropTypes.shape({
    isLoading: PropTypes.bool.isRequired,
  }),
};

const mapStateToProps = state => ({
  form: state.deepDiveEditor.form,
  status: state.deepDiveEditor.status,
  ui: state.deepDiveEditor.ui,
});

const mapDispatchToProps = {
  initializeDeepDiveForm,
  hydrateDeepDiveForm,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(DeepDiveEditorContainer);
